import React, { useState } from "react";
import { navigate } from "@reach/router";
import { AuthConsumer } from "../helpers/AuthContext";

// Step 1 of recovery: collect the email and ask the server to send a
// reset link + 6-digit code. The server replies generically (it won't
// say whether the account exists), so on success we always show the same
// "check your inbox" screen — which also offers a path to the code form.
const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = (forgotPassword) => {
    if (!email) {
      setMessage("Enter the email you registered with.");
      return;
    }
    setIsSubmitting(true);
    setMessage("");
    forgotPassword(email.trim().toLowerCase())
      .then(() => {
        setIsSubmitting(false);
        setSent(true);
      })
      .catch((err) => {
        setIsSubmitting(false);
        const msg =
          err?.response?.data?.message ||
          "Couldn't send the reset email. Please try again.";
        setMessage(msg);
      });
  };

  return (
    <AuthConsumer>
      {({ forgotPassword }) => (
        <div className="theme-scientific auth-page">
          <div className="auth-shell auth-shell--solo">
            <section className="auth-form-panel">
              <header className="auth-form-panel__header">
                <div className="sci-eyebrow">ICMOBI / Account recovery</div>
                <h2 className="auth-form-panel__title">
                  {sent ? "Check your inbox" : "Forgot your password?"}
                </h2>
                <p className="auth-form-panel__subtitle">
                  {sent
                    ? "If that email is registered, we've sent a 6-digit code. It expires in 15 minutes."
                    : "Enter your email and we'll send you a 6-digit code to set a new password."}
                </p>
              </header>

              {message && (
                <div className="sci-alert sci-alert--danger">
                  <div className="sci-alert__icon" />
                  <span>{message}</span>
                </div>
              )}

              {sent ? (
                <>
                  <div className="sci-alert sci-alert--success">
                    <div className="sci-alert__icon" />
                    <span>Reset code on its way to {email.trim().toLowerCase()}.</span>
                  </div>
                  <button
                    className="sci-btn sci-btn--primary sci-btn--full"
                    style={{ marginTop: 16 }}
                    onClick={() =>
                      navigate(
                        "/reset-password?email=" +
                          encodeURIComponent(email.trim().toLowerCase())
                      )
                    }
                  >
                    Enter code →
                  </button>
                  <button
                    className="sci-btn sci-btn--ghost sci-btn--full"
                    style={{ marginTop: 10 }}
                    onClick={() => {
                      setSent(false);
                      setMessage("");
                    }}
                  >
                    Use a different email
                  </button>
                </>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(forgotPassword);
                  }}
                >
                  <div className="sci-field">
                    <label
                      className="sci-field__label sci-field__label--req"
                      htmlFor="fp-email"
                    >
                      Email
                    </label>
                    <input
                      id="fp-email"
                      type="email"
                      className="sci-input"
                      placeholder="you@institution.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                  </div>

                  <button
                    type="submit"
                    className="sci-btn sci-btn--primary sci-btn--full"
                    disabled={isSubmitting}
                    style={{ marginTop: 8 }}
                  >
                    {isSubmitting ? "Sending…" : "Send reset code →"}
                  </button>
                </form>
              )}

              <div className="auth-footer-link">
                <span>Remembered it?</span>
                <a
                  href="/login"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/login");
                  }}
                >
                  Sign in →
                </a>
              </div>
            </section>
          </div>
        </div>
      )}
    </AuthConsumer>
  );
};

export default ForgotPassword;
